import {
  isAutoSubmitWidget,
  isLookupTool,
  isWidgetTool,
  isWidgetToolPart,
} from "@health/types/ui-tools";
import type {
  ChatClaimDisclaimerItem,
  ChatItem,
  ChatMessageGroup,
} from "@health/ui/components/chat/types";
import type { UIMessage } from "ai";
import {
  getFooterExtractor,
  getGateExtractor,
  getInlineWidgetsExtractor,
  hasNoBoundary,
  hasVisibleStatus,
} from "./chat-tool-config";
import {
  detectTraceGroupNext,
  getSaveHideTrace,
  isSaveIntakeFields,
  isSignalInsight,
  mapSignalPart,
  mapTextPart,
  mapTraceGroupItem,
  mapVisibleStatusItem,
  mapWidgetItem,
  siblingToolNamesFrom,
  type ToolPart,
  toolNameOf,
} from "./normalize-projections";

export type NormalizeOptions = {
  /** Include normally-invisible server tools (for debug surfaces). */
  includeInvisibleTools?: boolean;
  /** When true, marks the last assistant message's parts as live (streaming). */
  isStreaming?: boolean;
  /**
   * Synthetic items (hero, recap, greeting text, greeting/starter chips, etc.)
   * that precede the conversation. Merged into the first message group so the
   * greeting renders in the same visual step as the first assistant message —
   * rather than as its own standalone screen. When there are no messages, the
   * preamble becomes its own group.
   */
  preambleItems?: ChatItem[];
};

/**
 * Converts AI SDK `UIMessage[]` into `ChatMessageGroup[]`.
 *
 * Each group starts at the first assistant-role item after user-role items,
 * mirroring the visual grouping in the old MessageList. Handles: heading
 * dedup, invisible tool filtering, legacy alias resolution, tool-part state
 * mapping, TraceGroup detection, and group boundary detection.
 *
 * The function is a thin orchestrator: per-part projections live in
 * `normalize-projections.ts`, and all mutable group-building state is
 * encapsulated by `GroupBuilder` below.
 */
export function normalizeChatItems(
  messages: UIMessage[],
  options: NormalizeOptions = {},
): ChatMessageGroup[] {
  const { includeInvisibleTools = false, isStreaming = false, preambleItems } = options;

  const builder = new GroupBuilder();
  const seenHeadings = new Set<string>();
  const lastAssistantIdx = messages.findLastIndex((m) => m.role === "assistant");

  for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
    const message = messages[msgIdx];
    const parts = message.parts.filter((p) => p.type !== "step-start");
    const isLive = isStreaming && msgIdx === lastAssistantIdx;
    const siblingToolNames = siblingToolNamesFrom(parts);

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // --- Text parts ---
      if (part.type === "text") {
        const item = mapTextPart(message, i, part as { type: "text"; text?: string }, seenHeadings);
        if (item) builder.pushItem(item, message.role === "user");
        continue;
      }

      // --- Tool parts ---
      if (!part.type.startsWith("tool-")) continue;

      const toolPart = part as unknown as ToolPart;
      const toolName = toolNameOf(toolPart);

      // Dedup: skip duplicate visible-status server tools within a message.
      if (
        hasVisibleStatus(toolName) &&
        !isWidgetTool(toolName) &&
        parts[i + 1]?.type === part.type
      ) {
        continue;
      }

      // --- signal (status or insight) ---
      if (toolName === "signal") {
        const item = mapSignalPart(message, i, toolPart);
        if (item) {
          // Status items are user-role boundaries; insight items aren't.
          builder.pushItem(item, item.kind === "status");
        }
        continue;
      }

      // --- save_intake_fields + signal-insight pair (trace-group) ---
      const insightPart = detectTraceGroupNext(parts, i, toolName);
      if (insightPart) {
        if (!getSaveHideTrace(toolPart)) {
          builder.pushItem(
            mapTraceGroupItem(
              message,
              i,
              toolName,
              toolPart,
              insightPart,
              isLive,
              siblingToolNames,
            ),
            false,
          );
        }
        builder.closeAndGate(toolName, message.id, i, toolPart, false);
        i++; // also consumed the paired signal-insight
        continue;
      }

      // --- Visible-status server tools ---
      if (
        hasVisibleStatus(toolName) &&
        !isWidgetTool(toolName) &&
        (toolPart.state === "input-available" || toolPart.state === "output-available")
      ) {
        // F3 Option B / F10 Option B': a server tool may attach inline
        // widget payloads to its output (claim card, next-field show_chips
        // / show_text_input). Render them BEFORE the status item so they
        // land in the same group as the prose ack, ahead of the user
        // boundary the status push triggers.
        //
        // Per-entry collision guard: skip any synthesized widget whose
        // tool_name is already a sibling tool part in this message. Covers
        // partial deploys / cache transitions where both server and model
        // paths emit (model wins) — and applies uniformly to show_claim,
        // show_chips, show_text_input.
        const inlineExtractor = getInlineWidgetsExtractor(toolName);
        if (inlineExtractor) {
          const { widgetItems, footerItems } = inlineExtractor(message.id, i, toolPart);
          // Track which entries were emitted so co-emitted footers (claim
          // disclaimer derived from the show_claim entry) follow the same
          // suppression. The extractor emits widgets and footers in lockstep
          // by entry index — the prefix of `widgetItem.id` (`-inline-${idx}`)
          // matches the footer's id prefix.
          const emittedEntryPrefixes = new Set<string>();
          for (const widgetItem of widgetItems) {
            if (siblingToolNames.includes(widgetItem.toolName)) continue;
            builder.pushItem(widgetItem, false);
            // strip trailing "-widget" from the synthesized id to recover the entry prefix
            const prefix = widgetItem.id.replace(/-widget$/, "");
            emittedEntryPrefixes.add(prefix);
          }
          for (const footerItem of footerItems) {
            const matched = Array.from(emittedEntryPrefixes).some((p) =>
              footerItem.id.startsWith(p),
            );
            if (!matched) continue;
            builder.pushFooter(footerItem);
          }
        }
        // When the save declares hide_trace we skip both the status item
        // and the user-boundary marker so acknowledgment prose stays
        // visually grouped with prose that follows the save (no new step).
        if (!(isSaveIntakeFields(toolName) && getSaveHideTrace(toolPart))) {
          builder.pushItem(
            mapVisibleStatusItem(message, i, toolName, toolPart, isLive, siblingToolNames),
            true,
          );
        }
        builder.closeAndGate(toolName, message.id, i, toolPart, true);
        continue;
      }

      // --- Lookup tools + invisible server tools ---
      if (!isWidgetTool(toolName)) {
        const dropInvisible = toolPart.state === "output-available" && !includeInvisibleTools;
        if (dropInvisible || isLookupTool(toolName)) {
          if (isUserMessagePart(message, toolPart)) builder.markUserBoundary();
        }
        continue;
      }

      // --- Widget tools ---
      const widgetItem = mapWidgetItem(message.id, i, toolName, toolPart);
      if (!widgetItem) continue;

      if (hasNoBoundary(toolName)) {
        builder.pushWithoutBoundary(widgetItem);
        continue;
      }

      builder.pushItem(widgetItem, isUserMessagePart(message, toolPart));
      const footerItem = getFooterExtractor(toolName)?.(message.id, i, toolPart);
      if (footerItem) builder.pushFooter(footerItem);
    }
  }

  return builder.build(preambleItems);
}

// ---------------------------------------------------------------------------
// GroupBuilder — the only place that holds mutable chat-feed state.
//
// Encapsulates everything imperative about turning a stream of items into
// `ChatMessageGroup[]`: group boundary management (driven by user/assistant
// role transitions), footer collection, gate items (close + gate next),
// historic flagging on user-boundary flushes, and preamble merging.
//
// The orchestrator above stays declarative — every branch projects parts
// to items and tells the builder what to do with them.
// ---------------------------------------------------------------------------

/** Anything that participates in `historic` flushing (widgets and footer items). */
type Historicable = { historic?: boolean };

class GroupBuilder {
  private groups: ChatMessageGroup[] = [];

  /** Items in the currently-open group. */
  private currentItems: ChatItem[] = [];
  /** Footer items docked at the bottom of the currently-open group. */
  private currentFooterItems: ChatItem[] = [];
  /** Group id is derived from the first item's id. */
  private currentGroupStartId = "";
  /** Whether the most recent push was a user-role item — drives boundary detection. */
  private lastPushedWasUser = false;
  /** Whether the most recent push was a *submitted* user-role widget. Two
   *  consecutive submitted widgets in the same assistant message represent
   *  two distinct user moments (e.g. the SH recommendation chain — submitted
   *  carousel, then submitted vitality card after "another option"); without
   *  a finalize between them they'd merge into one group, since the regular
   *  role-transition check only fires user → assistant. Status items,
   *  transition cards, loading placeholders, and active widgets all reset
   *  this flag — only a fresh submitted widget right after another one
   *  triggers the split. */
  private lastPushedWasSubmittedWidget = false;
  /** When set, the next finalized group is gated by this id (stage transition). */
  private pendingGate: string | undefined;
  /** Items waiting for the next user-boundary flush to be marked `historic`. */
  private historicTracked: Historicable[] = [];

  /** Push an item into the current group. Honors role-transition boundaries
   *  (assistant content after a user-role item starts a new group) and
   *  flushes prior widgets/footers as `historic` on user-role pushes. */
  pushItem(item: ChatItem, isUser: boolean): void {
    const isSubmittedWidget =
      isUser && item.kind === "widget" && (item as { state?: string }).state === "submitted";

    // Two consecutive submitted user-role widgets → split into separate
    // steps. See `lastPushedWasSubmittedWidget` doc comment above.
    if (isSubmittedWidget && this.lastPushedWasSubmittedWidget && this.currentItems.length > 0) {
      this.finalizeCurrent();
    }
    if (!isUser && this.lastPushedWasUser && this.currentItems.length > 0) {
      this.finalizeCurrent();
    }
    if (this.currentItems.length === 0) this.currentGroupStartId = item.id;
    this.currentItems.push(item);
    this.lastPushedWasUser = isUser;
    this.lastPushedWasSubmittedWidget = isSubmittedWidget;

    // Flush BEFORE tracking this item so it isn't marked historic by its
    // own boundary. A user-role widget triggers the flush for prior
    // widgets, then joins the pending list so the *next* user interaction
    // can mark it historic in turn.
    if (isUser) this.flushHistoric();
    if (item.kind === "widget") this.historicTracked.push(item);
  }

  /** Dock a footer item to the current group. Tracked alongside widgets
   *  so the footer hides itself once the user moves past its source. */
  pushFooter(item: ChatClaimDisclaimerItem): void {
    this.currentFooterItems.push(item);
    this.historicTracked.push(item);
  }

  /** Push an auxiliary item without any group-boundary logic. The item joins
   *  whatever group is currently open (or starts one if empty), and does NOT
   *  update `lastPushedWasUser` — so the next real item's boundary decision
   *  is unaffected. Use for invisible/auto-submit widgets like
   *  `show_reasoning_trace` that should float alongside adjacent content. */
  pushWithoutBoundary(item: ChatItem): void {
    if (this.currentItems.length === 0) this.currentGroupStartId = item.id;
    this.currentItems.push(item);
    if (item.kind === "widget") this.historicTracked.push(item);
  }

  /** Mark a user boundary without pushing an item (for invisible/lookup
   *  tools that count as user-initiated but don't render). */
  markUserBoundary(): void {
    this.lastPushedWasUser = true;
    this.lastPushedWasSubmittedWidget = false;
    this.flushHistoric();
  }

  /** Run the registered gate extractor for `toolName` (no-op if none).
   *  Appends the projected item, closes the current group, and marks the
   *  next group as gated by the item's id. `isUser` matches `pushItem`
   *  semantics — trace-group branch passes `false`, visible-status passes
   *  `true`. */
  closeAndGate(
    toolName: string,
    messageId: string,
    partIdx: number,
    toolPart: ToolPart,
    isUser: boolean,
  ): void {
    const item = getGateExtractor(toolName)?.(messageId, partIdx, toolPart);
    if (!item) return;
    this.pushItem(item, isUser);
    this.finalizeCurrent();
    this.pendingGate = item.id;
  }

  /** Finalize remaining state and return the assembled groups. Optionally
   *  merges `preambleItems` into the first group (or creates a preamble
   *  group when there are no messages). */
  build(preambleItems?: ChatItem[]): ChatMessageGroup[] {
    this.finalizeCurrent();
    if (preambleItems && preambleItems.length > 0) this.mergePreamble(preambleItems);
    return this.groups;
  }

  // -- internals --------------------------------------------------------

  private finalizeCurrent(): void {
    if (this.currentItems.length === 0) return;
    this.groups.push({
      id: `group-${this.currentGroupStartId}`,
      items: this.currentItems,
      ...(this.currentFooterItems.length > 0 && { footerItems: this.currentFooterItems }),
      ...(this.pendingGate && { gatedBy: this.pendingGate }),
    });
    this.currentItems = [];
    this.currentFooterItems = [];
    this.currentGroupStartId = "";
    this.pendingGate = undefined;
  }

  private flushHistoric(): void {
    for (const x of this.historicTracked) x.historic = true;
    this.historicTracked = [];
  }

  private mergePreamble(preambleItems: ChatItem[]): void {
    if (this.groups.length > 0) {
      this.groups[0] = {
        ...this.groups[0],
        id: "preamble",
        items: [...preambleItems, ...this.groups[0].items],
      };
    } else {
      this.groups.push({ id: "preamble", items: [...preambleItems] });
    }
  }
}

// ---------------------------------------------------------------------------
// Visible-content fingerprint — used by ChatPanel to track whether any
// user-visible content (streamed text or UI widgets) has changed in the
// last assistant message. Server-only tool calls update messages too but
// produce no visible output, so a plain `state.messages` dependency would
// prematurely clear the loader while nothing is shown.
// ---------------------------------------------------------------------------

/**
 * Returns a stable string that changes only when the last assistant message
 * produces visible output: streamed text length or a widget tool's id/state.
 * Returns `null` when the last part is neither text nor a widget.
 */
export function getVisibleMessageFingerprint(message: UIMessage | undefined): string | null {
  if (!message) return null;
  const lastPart = message.parts.at(-1);
  if (!lastPart) return null;
  const partsLength = message.parts.length;
  if (lastPart.type === "text") {
    return `t:${(lastPart as { text: string }).text.length}|${partsLength}`;
  }
  if (isWidgetToolPart(lastPart)) {
    const tp = lastPart as { toolCallId?: string; state?: string };
    return `w:${tp.toolCallId}:${tp.state}|${partsLength}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public predicate — used by ChatPanel to decide whether the input bar
// should show a "waiting for tool" state, and by the orchestrator above
// to detect user-boundary tool parts.
// ---------------------------------------------------------------------------

/**
 * Determines if a message part represents user-initiated content.
 *
 * User text parts and submitted interactive widgets count, as do
 * visible-status server tools, lookup tools, and signal-status (signal
 * insight is reflective AI-generated content, not a user boundary).
 *
 * Auto-submit widgets (`show_claim`, `show_testimonial`, etc.) are
 * display-only — they acknowledge themselves and never represent user
 * choice — so they're explicitly excluded from user-boundary
 * classification, even when their domain is `lookup`.
 */
export function isUserMessagePart(
  message: Pick<UIMessage, "role">,
  toolPart: { type: string; state?: string; output?: unknown; input?: unknown },
): boolean {
  if (message.role === "user" && toolPart.type === "text") return true;
  if (!toolPart.type.startsWith("tool-")) return false;
  if (message.role !== "assistant") return false;

  const toolName = toolNameOf(toolPart);
  if (isAutoSubmitWidget(toolName)) return false;

  const isSignalStatus = toolName === "signal" && !isSignalInsight(toolPart);
  // Visible-status server tools (e.g. `check_eligibility`, `save_intake_fields`)
  // and signal-status mark a user-role moment as soon as they appear — they
  // have no streaming widget state to wait on.
  if (hasVisibleStatus(toolName) || isSignalStatus) return true;

  // For everything else (lookup tools, widget tools), we need a finalized
  // output before declaring a user-role boundary. During `input-streaming` /
  // `input-available`, the widget is still loading or actively prompting — it
  // hasn't produced a user moment yet. Mistakenly counting it here causes
  // consecutive interactive widgets in the same assistant message (e.g. the
  // sexual-health recommendation chain where the user clicks "another option"
  // and the agent immediately calls `show_meducation` again) to collapse into
  // a single group while the new tool call streams in.
  if (toolPart.state !== "output-available" || !toolPart.output) return false;

  const output = toolPart.output as Record<string, unknown>;
  // Interactive widget that still requires user input — not a boundary yet.
  if (output._requiresInteraction) return false;

  // Lookup tools (server-only `lookup_testimonial`, plus widget lookups like
  // `show_meducation` / `show_product` whose output isn't waiting on the user)
  // count as user-role moments once their output is available.
  if (isLookupTool(toolName)) return true;

  // Intake / non-lookup widget tools (`show_chips`, `show_form`, etc.) only
  // become user-role boundaries once the user has actually submitted —
  // skipped or cancelled outputs don't open a new step. Accept both the
  // legacy `{ action: "cancelled" }` shape (PhotoUpload, ShippingAddress,
  // etc.) and the canonical `{ kind: "dismissed" }` shape used by AuthWidget.
  const finished = !output.skipped && output.action !== "cancelled" && output.kind !== "dismissed";
  if (finished && isWidgetTool(toolName)) return true;

  return false;
}
